import { CHART_OPTIONS_BY_FEATURE } from './chartOptionsByFeature';

export type ChartOptionGroupId = 'time' | 'window' | 'ahead' | 'budget';

interface ChartOptionChoice {
    // the chart's own switch label, written lower-case (what chartOptionsByFeature.ts holds)
    key: string;
    label: string;
}

interface ChartOptionGroup {
    id: ChartOptionGroupId;
    title: string;
    description: string;
    choices: ChartOptionChoice[];
}

// The period switches the dashboard's charts have in common, in the order the drawer lists them. A choice is only offered when a chart on the
// current dashboard has it; matching a chart's switch is by its label, so a chart without that label keeps its own setting.
export const CHART_OPTION_GROUPS: ChartOptionGroup[] = [
    {
        id: 'time',
        title: 'Time period',
        description: 'The current or previous week, month or financial year.',
        choices: [
            { key: 'today', label: 'Today' },
            { key: 'yesterday', label: 'Yesterday' },
            { key: 'this week', label: 'This Week' },
            { key: 'last week', label: 'Last Week' },
            { key: 'this month', label: 'This Month' },
            { key: 'previous month', label: 'Previous Month' },
            { key: 'last month', label: 'Last Month' },
            { key: 'financial year', label: 'Financial Year' },
            { key: 'previous financial year', label: 'Previous Financial Year' },
            { key: 'financial year to date', label: 'Financial Year to Date' },
        ],
    },
    {
        id: 'window',
        title: 'Rolling window',
        description: 'The last number of days, months or years.',
        choices: [
            { key: 'last 7 days', label: 'Last 7 days' },
            { key: 'last 14 days', label: 'Last 14 days' },
            { key: 'last 30 days', label: 'Last 30 days' },
            { key: 'last 90 days', label: 'Last 90 days' },
            { key: 'last 3 months', label: 'Last 3 months' },
            { key: 'last 6 months', label: 'Last 6 months' },
            { key: 'last 12 months', label: 'Last 12 months' },
            { key: 'last 2 years', label: 'Last 2 years' },
            { key: 'all time', label: 'All time' },
        ],
    },
    {
        id: 'ahead',
        title: 'Looking ahead',
        description: 'Forecasts for the days, months or years to come.',
        choices: [
            { key: 'next 7 days', label: 'Next 7 days' },
            { key: 'next 30 days', label: 'Next 30 days' },
            { key: 'next month', label: 'Next Month' },
            { key: 'next 6 months', label: 'Next 6 Months' },
            { key: 'next 12 months', label: 'Next 12 Months' },
            { key: 'next 3 years', label: 'Next 3 Years' },
        ],
    },
    {
        id: 'budget',
        title: 'Budget view',
        description: 'The KPI Summary against budget.',
        choices: [
            { key: 'week to date', label: 'Week to Date' },
            { key: 'month to date', label: 'Month to Date' },
            { key: 'year to date', label: 'Year to Date' },
            { key: 'previous month ytd', label: 'Previous Month YTD' },
            { key: 'full year budget forecast', label: 'Full Year Budget Forecast' },
        ],
    },
];

// the labels a chart's switch shows, by the chart's feature name
export const getChartOptionLabels = (featureName: string): string[] => CHART_OPTIONS_BY_FEATURE[featureName] || [];

// How many of the dashboard's charts have this option on their own switch.
export const countChartsWithOption = (featureNames: string[], optionKey: string) =>
    featureNames.filter((featureName) => getChartOptionLabels(featureName).includes(optionKey)).length;

// How many of the dashboard's charts have at least one of these options.
export const countChartsWithAnyOption = (featureNames: string[], optionKeys: string[]) =>
    featureNames.filter((featureName) => getChartOptionLabels(featureName).some((label) => optionKeys.includes(label))).length;

export const normaliseChartOptionLabel = (label: string) => label.toLowerCase().replace(/\s+/g, ' ').trim();
